/**
 * AdminVisibility.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import './AdminVisibility.css';

/** Settings rows under Ratings & comments; BOTH = legacy key when no split rows saved. */
const RATINGS_AND_COMMENTS_DEFS = [
  {
    legacyKey: 'user_ratings_articles_enabled',
    ratingsKey: 'user_ratings_articles_ratings_enabled',
    commentsKey: 'user_ratings_articles_comments_enabled',
    title: 'Articles & content pages',
    subtitle:
      'Pages at /page/… (schedules, articles, etc.). Excludes tune pages and book-index pages.',
  },
  {
    legacyKey: 'user_ratings_tunes_enabled',
    ratingsKey: 'user_ratings_tunes_ratings_enabled',
    commentsKey: 'user_ratings_tunes_comments_enabled',
    title: 'Tune pages',
    subtitle: 'Tune / notation pages (TunePage)',
  },
  {
    legacyKey: 'user_ratings_abc_tune_enabled',
    ratingsKey: 'user_ratings_abc_tune_ratings_enabled',
    commentsKey: 'user_ratings_abc_tune_comments_enabled',
    title: 'ABC tune assets',
    subtitle: 'Standalone ABC viewer (/tune-asset/…)',
  },
  {
    legacyKey: 'user_ratings_products_enabled',
    ratingsKey: 'user_ratings_products_ratings_enabled',
    commentsKey: 'user_ratings_products_comments_enabled',
    title: 'Products',
    subtitle: 'Product detail pages',
  },
];

const ALL_FEEDBACK_SETTING_KEYS = new Set(
  RATINGS_AND_COMMENTS_DEFS.flatMap((d) => [d.legacyKey, d.ratingsKey, d.commentsKey]),
);

function defForFeedbackSettingKey(settingKey) {
  return RATINGS_AND_COMMENTS_DEFS.find(
    (d) => d.legacyKey === settingKey || d.ratingsKey === settingKey || d.commentsKey === settingKey,
  );
}

function rowFor(settings, key) {
  return settings.find((s) => s.setting_key === key);
}

/** Mirrors server getFeedbackPolicy: split row wins; single split ⇒ other channel off; else BOTH (legacy). */
function effectiveRatingsOn(settings, def) {
  const hasR = rowFor(settings, def.ratingsKey) != null;
  const hasC = rowFor(settings, def.commentsKey) != null;
  const leg = rowFor(settings, def.legacyKey);
  const legacyOn = leg != null && leg.setting_value === 'true';
  if (hasR) return rowFor(settings, def.ratingsKey).setting_value === 'true';
  if (hasC) return false;
  return legacyOn;
}

function effectiveCommentsOn(settings, def) {
  const hasR = rowFor(settings, def.ratingsKey) != null;
  const hasC = rowFor(settings, def.commentsKey) != null;
  const leg = rowFor(settings, def.legacyKey);
  const legacyOn = leg != null && leg.setting_value === 'true';
  if (hasC) return rowFor(settings, def.commentsKey).setting_value === 'true';
  if (hasR) return false;
  return legacyOn;
}

const AdminVisibility = () => {
  const [pages, setPages] = useState([]);
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pages');
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pagesResponse, settingsResponse] = await Promise.all([
        fetch('/api/visibility/admin/pages', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }),
        fetch('/api/visibility/settings', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }),
      ]);

      if (!pagesResponse.ok || !settingsResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const pagesData = await pagesResponse.json();
      const settingsData = await settingsResponse.json();

      setPages(pagesData.pages || []);
      setSettings(settingsData.settings || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedbackList = useCallback(async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const res = await fetch('/api/content-feedback/admin/list?limit=100', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Failed to load feedback');
      const data = await res.json();
      setFeedbackItems(data.items || []);
    } catch (e) {
      setFeedbackError(e.message || 'Failed to load feedback');
      setFeedbackItems([]);
    } finally {
      setFeedbackLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'feedback') fetchFeedbackList();
  }, [activeTab, fetchFeedbackList]);

  const updatePageVisibility = async (pageKey, updates) => {
    try {
      const response = await fetch(`/api/visibility/pages/${pageKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('Failed to update page visibility');
      }

      setPages((prevPages) =>
        prevPages.map((page) => (page.page_key === pageKey ? { ...page, ...updates } : page)),
      );

      setSuccessMessage('Page visibility updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const updateWebsiteSetting = async (settingKey, settingValue) => {
    try {
      const response = await fetch(`/api/visibility/settings/${settingKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ setting_value: settingValue }),
      });

      if (!response.ok) {
        throw new Error('Failed to update website setting');
      }

      setSettings((prevSettings) => {
        const idx = prevSettings.findIndex((s) => s.setting_key === settingKey);
        if (idx >= 0) {
          return prevSettings.map((s, i) =>
            i === idx ? { ...s, setting_value: settingValue } : s,
          );
        }
        const def = defForFeedbackSettingKey(settingKey);
        const description =
          def != null
            ? settingKey === def.ratingsKey
              ? `Star ratings — ${def.title}`
              : settingKey === def.commentsKey
                ? `Text comments — ${def.title}`
                : def.subtitle
            : '';
        return [
          ...prevSettings,
          {
            setting_key: settingKey,
            setting_value: settingValue,
            setting_type: 'boolean',
            description,
          },
        ];
      });

      setSuccessMessage('Website setting updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePageToggle = (pageKey, field, value) => {
    updatePageVisibility(pageKey, { [field]: value });
  };

  const handleSettingChange = (settingKey, value) => {
    updateWebsiteSetting(settingKey, value);
  };

  const handleDeleteFeedback = async (id) => {
    if (!window.confirm('Remove this rating/comment permanently?')) return;
    setDeletingId(id);
    setFeedbackError(null);
    try {
      const res = await fetch(`/api/content-feedback/admin/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Delete failed');
      }
      setFeedbackItems((prev) => prev.filter((row) => row.id !== id));
      setSuccessMessage('Feedback removed');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (e) {
      setFeedbackError(e.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="admin-visibility-loading">Loading visibility settings...</div>;
  }

  if (error) {
    return <div className="admin-visibility-error">Error: {error}</div>;
  }

  return (
    <div className="admin-visibility">
      <div className="admin-visibility-header">
        <h2>Website Visibility Control</h2>
        <p>Control which pages and features are visible to different user types</p>
      </div>

      {successMessage && <div className="admin-visibility-success">{successMessage}</div>}

      <div className="admin-visibility-tabs">
        <button
          type="button"
          className={activeTab === 'pages' ? 'active' : ''}
          onClick={() => setActiveTab('pages')}
        >
          Page Visibility
        </button>
        <button
          type="button"
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          Website Settings
        </button>
        <button
          type="button"
          className={activeTab === 'feedback' ? 'active' : ''}
          onClick={() => setActiveTab('feedback')}
        >
          User feedback
        </button>
      </div>

      {activeTab === 'pages' && (
        <div className="admin-visibility-pages">
          <div className="pages-grid">
            {pages.map((page) => (
              <div key={page.page_key} className="page-visibility-card">
                <div className="page-header">
                  <h3>{page.page_name}</h3>
                  <span className="page-key">/{page.page_key}</span>
                </div>

                <div className="page-controls">
                  <div className="control-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={page.is_visible}
                        onChange={(e) => handlePageToggle(page.page_key, 'is_visible', e.target.checked)}
                      />
                      Visible to All
                    </label>
                  </div>

                  <div className="control-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={page.is_visible_to_guests}
                        onChange={(e) =>
                          handlePageToggle(page.page_key, 'is_visible_to_guests', e.target.checked)
                        }
                        disabled={!page.is_visible}
                      />
                      Guests
                    </label>
                  </div>

                  <div className="control-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={page.is_visible_to_customers}
                        onChange={(e) =>
                          handlePageToggle(page.page_key, 'is_visible_to_customers', e.target.checked)
                        }
                        disabled={!page.is_visible}
                      />
                      Customers
                    </label>
                  </div>

                  <div className="control-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={page.is_visible_to_wholesalers}
                        onChange={(e) =>
                          handlePageToggle(
                            page.page_key,
                            'is_visible_to_wholesalers',
                            e.target.checked,
                          )
                        }
                        disabled={!page.is_visible}
                      />
                      Wholesalers
                    </label>
                  </div>

                  <div className="control-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={page.is_visible_to_admins}
                        onChange={(e) =>
                          handlePageToggle(page.page_key, 'is_visible_to_admins', e.target.checked)
                        }
                        disabled={!page.is_visible}
                      />
                      Admins
                    </label>
                  </div>
                </div>

                <div className="page-advanced">
                  <div className="control-group">
                    <label>Maintenance Message:</label>
                    <textarea
                      value={page.maintenance_message || ''}
                      onChange={(e) =>
                        handlePageToggle(page.page_key, 'maintenance_message', e.target.value)
                      }
                      placeholder="Message shown when page is not visible"
                      rows="2"
                    />
                  </div>

                  <div className="control-group">
                    <label>Redirect URL:</label>
                    <input
                      type="url"
                      value={page.redirect_url || ''}
                      onChange={(e) =>
                        handlePageToggle(page.page_key, 'redirect_url', e.target.value)
                      }
                      placeholder="URL to redirect to when page is not visible"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <>
          <div className="admin-visibility-settings admin-visibility-ratings-group">
            <h3 className="settings-group-title">Ratings & comments (signed-in users)</h3>
            <p className="settings-group-intro">
              Use <strong>Star ratings</strong> and <strong>Text comments</strong> for ratings-only,
              comments-only, or both. <strong>BOTH (rating and commenting)</strong> applies when neither
              split setting has been saved yet (same as the original single switch). Saving only one split
              turns the other off unless you enable it separately. Refresh a public page after changes.
            </p>
            <div className="settings-grid">
              {RATINGS_AND_COMMENTS_DEFS.map((def) => {
                const leg = rowFor(settings, def.legacyKey);
                const description = leg && leg.description ? leg.description : def.subtitle;
                const ratingsOn = effectiveRatingsOn(settings, def);
                const commentsOn = effectiveCommentsOn(settings, def);
                const legacyOn = leg != null ? leg.setting_value === 'true' : false;
                return (
                  <div key={def.legacyKey} className="setting-card setting-card-featured">
                    <div className="setting-header">
                      <h3>{def.title}</h3>
                      <p className="setting-description">{description}</p>
                    </div>
                    <div className="feedback-split-toggles">
                      <label className="feedback-split-row">
                        <span>Star ratings</span>
                        <span className="setting-control">
                          <label className="setting-toggle">
                            <input
                              type="checkbox"
                              checked={ratingsOn}
                              onChange={(e) =>
                                handleSettingChange(def.ratingsKey, e.target.checked.toString())
                              }
                            />
                            <span className="toggle-slider" />
                          </label>
                        </span>
                      </label>
                      <label className="feedback-split-row">
                        <span>Text comments</span>
                        <span className="setting-control">
                          <label className="setting-toggle">
                            <input
                              type="checkbox"
                              checked={commentsOn}
                              onChange={(e) =>
                                handleSettingChange(def.commentsKey, e.target.checked.toString())
                              }
                            />
                            <span className="toggle-slider" />
                          </label>
                        </span>
                      </label>
                      <label className="feedback-split-row feedback-split-row-legacy">
                        <span>BOTH (rating and commenting)</span>
                        <span className="setting-control">
                          <label className="setting-toggle">
                            <input
                              type="checkbox"
                              checked={legacyOn}
                              onChange={(e) =>
                                handleSettingChange(def.legacyKey, e.target.checked.toString())
                              }
                            />
                            <span className="toggle-slider" />
                          </label>
                        </span>
                      </label>
                      <p className="feedback-key-hint">
                        <code>{def.ratingsKey}</code> · <code>{def.commentsKey}</code> ·{' '}
                        <code>{def.legacyKey}</code>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="admin-visibility-settings">
            <h3 className="settings-group-title">Other website settings</h3>
            <div className="settings-grid">
              {settings
                .filter((s) => !ALL_FEEDBACK_SETTING_KEYS.has(s.setting_key))
                .map((setting) => (
                  <div key={setting.setting_key} className="setting-card">
                    <div className="setting-header">
                      <h3>{setting.setting_key.replace(/_/g, ' ').toUpperCase()}</h3>
                      <p className="setting-description">{setting.description}</p>
                    </div>

                    <div className="setting-control">
                      {setting.setting_type === 'boolean' ? (
                        <label className="setting-toggle">
                          <input
                            type="checkbox"
                            checked={setting.setting_value === 'true'}
                            onChange={(e) =>
                              handleSettingChange(setting.setting_key, e.target.checked.toString())
                            }
                          />
                          <span className="toggle-slider" />
                        </label>
                      ) : setting.setting_type === 'number' ? (
                        <input
                          type="number"
                          value={setting.setting_value}
                          onChange={(e) =>
                            handleSettingChange(setting.setting_key, e.target.value)
                          }
                          className="setting-input"
                        />
                      ) : (
                        <input
                          type="text"
                          value={setting.setting_value}
                          onChange={(e) =>
                            handleSettingChange(setting.setting_key, e.target.value)
                          }
                          className="setting-input"
                        />
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'feedback' && (
        <div className="admin-visibility-feedback">
          <h3 className="settings-group-title">User-submitted ratings & comments</h3>
          <p className="settings-group-intro">
            Remove spam or inappropriate entries. Deleting a row removes that user&apos;s feedback for
            that item.
          </p>
          {feedbackError && <div className="admin-visibility-error admin-feedback-banner">{feedbackError}</div>}
          {feedbackLoading ? (
            <p className="admin-visibility-loading-inline">Loading…</p>
          ) : (
            <div className="admin-feedback-table-wrap">
              <table className="admin-feedback-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>User</th>
                    <th>Target</th>
                    <th>Stars</th>
                    <th>Comment</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {feedbackItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="admin-feedback-empty">
                        No feedback yet.
                      </td>
                    </tr>
                  ) : (
                    feedbackItems.map((row) => (
                      <tr key={row.id}>
                        <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : ''}</td>
                        <td>
                          {[row.first_name, row.last_name].filter(Boolean).join(' ').trim() ||
                            row.username}
                        </td>
                        <td>
                          <code>
                            {row.target_type}:{row.target_id}
                          </code>
                        </td>
                        <td>{row.rating != null ? `${row.rating}/5` : '—'}</td>
                        <td className="admin-feedback-comment-cell">
                          {row.comment ? (
                            <span title={row.comment}>{row.comment.length > 80 ? `${row.comment.slice(0, 80)}…` : row.comment}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="admin-feedback-delete"
                            disabled={deletingId === row.id}
                            onClick={() => handleDeleteFeedback(row.id)}
                          >
                            {deletingId === row.id ? '…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          <p className="admin-feedback-refresh">
            <button type="button" className="admin-feedback-refresh-btn" onClick={fetchFeedbackList}>
              Refresh list
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminVisibility;
